import { formatEther, formatUnits } from 'ethers/lib/utils';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import bnbStaking from '../../common/svg/starsStaking/bnbStaking.svg';
import starsStaking from '../../common/svg/starsStaking/starsStaking.svg';
import type { PoolInfo } from '../../containers/pages/starsStaking/StarsStakingPage.container';
import { CoinGeckoContext } from '../../contexts/app-state-context/coingecko/coingecko.context';
import { useStarsEthLpTokenContract } from '../../hooks/lp-token-contract.hooks';
import { useStarStakingContract } from '../../hooks/stars-staking.hook';
import styles from './StakingCarouselSlide.module.css';

export interface StakingCarouselSlideProps {
  mobileView: boolean;
  currentSlide: number;
}

export const StakingCarouselSlide: React.FC<StakingCarouselSlideProps> =
  props => {
    const { starsUsdRate: starsToUSDRatio, bnbUsdRate: bnbToUSDRatio } =
      useContext(CoinGeckoContext);
    const [totalStarsStaked, setTotalStarsStaked] = useState(0);
    const [totalUSDStaked, setTotalUSDStaked] = useState(0);
    const [intervalStarted, setIntervalStarted] = useState(false);
    const [intervalStarted2, setIntervalStarted2] = useState(false);
    const [livePoolInfo, setLivePoolInfo] = useState<PoolInfo[]>([]);

    const history = useHistory();

    const { getStarsEthTotalSupply, getStarsEthReserves, initialized } =
      useStarsEthLpTokenContract(true);

    const { getPoolLength, getPool, contractInitialized } =
      useStarStakingContract(true);

    const poolInfo = [
      { name: 'VIP POOL', apr: '12.55' },
      { name: 'BACKSTAGE POOL', apr: '24.62' },
      { name: "THE OSCAR'S POOL", apr: '514.93' },
      { name: 'RED CARPET POOL', apr: '36.93' },
    ];

    const goToStaking = () => {
      history.push(`/platform/starsStaking`);
    };

    const openLearnMore = () => {
      const newWindow = window.open(
        'https://www.mogulproductions.com/post/introducing-stars-staking',
        '_blank',
      );
      if (newWindow) {
        newWindow.focus();
      }
    };

    useEffect(() => {
      const setLiveInfo = async () => {
        if (contractInitialized) {
          const newPools: PoolInfo[] = [];
          const length = await getPoolLength();
          for (let x = 0; x < length.poolLength; x++) {
            const pool = (await getPool(x)).pool;
            if (pool !== undefined) {
              let apr = 0;
              if (x === 2) {
                const starsBnbTotalSupply = parseFloat(
                  formatEther(await getStarsEthTotalSupply()),
                );
                const currentBnbStarsReserves = await getStarsEthReserves();
                const bnbReserves = parseFloat(
                  formatEther(currentBnbStarsReserves[0]),
                );
                const starsReserves = parseFloat(
                  formatEther(currentBnbStarsReserves[1]),
                );
                const LP_RESERVE_USD_VALUE =
                  starsReserves * starsToUSDRatio + bnbReserves * bnbToUSDRatio;
                const starsUSDPerDay =
                  28660 *
                  parseFloat(formatUnits(pool[6], 18)) *
                  starsToUSDRatio;
                const lpUSD =
                  (parseFloat(formatUnits(pool[7], 18)) *
                    LP_RESERVE_USD_VALUE) /
                  starsBnbTotalSupply;
                const daily = starsUSDPerDay / lpUSD;
                apr = 365 * daily * 100 === Infinity ? 0 : 365 * daily * 100;
              } else {
                const starsPerDay =
                  28660 * parseFloat(formatUnits(pool[6], 18));
                const daily =
                  starsPerDay / parseFloat(formatUnits(pool[7], 18));
                apr = 365 * daily * 100 === Infinity ? 0 : 365 * daily * 100;
              }

              newPools.push({
                contractPoolInfo: pool,
                apr,
                starsToUSDRatio,
                bnbToUSDRatio,
              });
            }
          }

          setLivePoolInfo(newPools);
        }
      };

      if (contractInitialized && initialized) {
        setIntervalStarted(true);
        void setLiveInfo();

        // Fetch every five seconds
        if (!intervalStarted) {
          const interval = setInterval(() => {
            void setLiveInfo();
          }, 5000);
          return () => clearInterval(interval);
        }
      }
    }, [contractInitialized]);

    useEffect(() => {
      const getTotalDeposit = async () => {
        if (contractInitialized && initialized) {
          try {
            let total = 0;
            let totalUSD = 0;

            const length = await getPoolLength();
            for (let x = 0; x < length.poolLength; x++) {
              const pool = await getPool(x);
              if (x === 2 && pool.pool) {
                // lp contract
                const currentLpTotalSupply = parseFloat(
                  formatEther(await getStarsEthTotalSupply()),
                );

                // lp reserves
                const currentBnbStarsReserves = await getStarsEthReserves();

                const lpRatio =
                  parseFloat(formatUnits(pool.pool[7], 18)) /
                  currentLpTotalSupply;
                const lpToStars =
                  parseFloat(formatEther(currentBnbStarsReserves[1])) * lpRatio;
                const lpToBnb =
                  parseFloat(formatEther(currentBnbStarsReserves[0])) * lpRatio;

                total += lpToStars;
                totalUSD += bnbToUSDRatio * lpToBnb;
              } else if (pool.pool) {
                total += parseFloat(formatUnits(pool.pool[7], 18));
              }
            }

            totalUSD += total * starsToUSDRatio;
            setTotalStarsStaked(total);
            setTotalUSDStaked(totalUSD);
          } catch (e) {
            window.Sentry?.captureException(e, {
              tags: {
                file: 'StakingCarouselSlide.component.tsx',
                function: 'use effect to get stars/eth conversions',
              },
            });
            console.error(e);
          }
        }
      };

      if (contractInitialized && initialized) {
        setIntervalStarted2(true);
        void getTotalDeposit();

        // Fetch every five seconds
        if (!intervalStarted2) {
          const interval = setInterval(() => {
            void getTotalDeposit();
          }, 5000);
          return () => clearInterval(interval);
        }
      }
    }, [contractInitialized, initialized]);

    return (
      <div
        className={styles.carouselBanner + ' w-full'}
        style={
          2 === props.currentSlide ? { display: 'block' } : { display: 'none' }
        }
      >
        <div
          className="w-full h-full flex xl:justify-between justify-center flex-wrap-reverse box-border lg:p-8 lg:py-10 xl:py-12 md:p-8 p-10 relative"
          style={{
            background: 'black',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '50% 50%',
            borderRadius: 16,
          }}
        >
          <div
            className={
              styles.overlay +
              ' w-full h-full absolute top-0 left-0 z-0 rounded-lg'
            }
          />
          <div
            className={
              'lg:w-7/12 w-full flex flex-col items-center lg:items-start box-border z-10 h-auto lg:text-left text-center'
            }
          >
            <div
              className={
                styles.collectionName +
                ' xl:text-7xl lg:text-5xl md:text-6xl text-5xl mb-3 sm:mb-2 font-extrabold'
              }
            >
              Staking
            </div>
            <div
              className={
                styles.description +
                ' xl:text-base lg:text-sm text-lg xl:pr-10 lg:pr-6 font-bold'
              }
            >
              Staking means to lock your tokens to earn rewards. Rewards can be
              accumulated passively just for locking your MOGUL tokens for a
              defined period of time. Staking rewards are received in the form
              of MOGUL tokens.
            </div>
            <div className="flex lg:flex-row flex-col lg:gap-4 gap-6 xl:text-2xl lg:text-xl md:text-3xl text-2xl font-extrabold mt-10">
              <div className={styles.collectionSubHeader}>
                MOGUL LOCKED ACROSS POOLS
                <div
                  className={
                    styles.currency +
                    ' xl:text-5xl lg:text-4xl md:text-5xl text-4xl mt-4'
                  }
                >
                  {totalStarsStaked !== 0 && !Number.isNaN(totalStarsStaked)
                    ? `${parseFloat(
                        totalStarsStaked.toFixed(0),
                      ).toLocaleString()} MOGUL`
                    : 'N/A'}
                </div>
              </div>

              <div className={styles.collectionSubHeader}>
                TOTAL VALUE LOCKED
                <div
                  className={
                    styles.currency +
                    ' xl:text-5xl lg:text-4xl md:text-5xl text-4xl mt-4'
                  }
                >
                  {totalUSDStaked !== 0 && !Number.isNaN(totalUSDStaked)
                    ? `$${parseFloat(
                        totalUSDStaked.toFixed(0),
                      ).toLocaleString()} USD`
                    : 'N/A'}
                </div>
              </div>
            </div>
            <div className="w-full flex items-center gap-6 box-border mt-auto">
              <div
                aria-hidden
                className={
                  styles.learnButton +
                  ' w-1/2 lg:w-5/12 xl:w-64 h-10 lg:h-12 text-xs sm:text-sm	font-semibold	flex justify-center items-center cursor-pointer'
                }
                onClick={() => goToStaking()}
              >
                STAKE MY MOGUL
              </div>
              <div
                aria-hidden
                className={
                  styles.viewButton +
                  ' w-1/2 lg:w-5/12 xl:w-64 h-10 lg:h-12 text-xs sm:text-sm font-semibold flex justify-center items-center text-center cursor-pointer pl-1'
                }
                onClick={() => openLearnMore()}
              >
                LEARN MORE
              </div>
            </div>
          </div>
          {!props.mobileView ? (
            <div
              className={
                'w-5/12 pl-2 box-border z-10 flex flex-col justify-between gap-8 lg:items-end items-center'
              }
            >
              {poolInfo.map((pool, index) => {
                return (
                  <div className="w-full" key={pool.name}>
                    {pool.name === 'THE OSCAR’S POOL' ? (
                      <div
                        className={
                          styles.poolStats +
                          ' w-full xl:px-3 xl:py-4 px-2 py-3 xl:text-lg text-sm box-border font-bold flex items-center xl:gap-2 gap-1'
                        }
                      >
                        <img
                          height="24"
                          src={bnbStaking}
                          alt="bnbStakingIcon"
                        />
                        <img
                          className={'-ml-5'}
                          height="24"
                          src={starsStaking}
                          alt="starStakingIcon"
                        />
                        {pool.name}
                        <div
                          className={
                            styles.statSubHeader + ' xl:text-lg text-sm ml-auto'
                          }
                        >
                          {livePoolInfo[index] &&
                          livePoolInfo[index].apr !== 0 &&
                          !Number.isNaN(livePoolInfo[index].apr)
                            ? `APR: ${livePoolInfo[index].apr.toFixed(2)}%`
                            : 'N/A'}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={
                          styles.poolStats +
                          ' w-full xl:px-3 xl:py-4 px-2 py-3 xl:text-lg text-sm box-border font-bold flex items-center xl:gap-2 gap-1'
                        }
                        key={pool.name}
                      >
                        <img
                          height="24"
                          src={starsStaking}
                          alt="starStakingIcon"
                        />
                        {pool.name}
                        <div
                          className={
                            styles.statSubHeader + ' xl:text-lg text-sm ml-auto'
                          }
                        >
                          {livePoolInfo[index] &&
                          livePoolInfo[index].apr !== 0 &&
                          !Number.isNaN(livePoolInfo[index].apr)
                            ? `APR: ${livePoolInfo[index].apr.toFixed(2)}%`
                            : 'N/A'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  };
